import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import Modal from "../Modal";
import { Dialog } from "@headlessui/react";
import { useSystemLogo } from "../../context/systemConfigContext";

const CallerList = ({
    sendJsonMessage,
    userData,
    lastJsonMessage,
    selectedMedicalOffice,
    setStep,
    consultationType,
}) => {
    let didInit = false;
    const [patientQueue, setPatientQueue] = useState([]);
    const [pendingAppointments, setPendingAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [turnToSkip, setTurnToSkip] = useState(null);
    const { logo } = useSystemLogo();
    const [lastSyncTime, setLastSyncTime] = useState(new Date());

    const getPatientQueue = () => {
        sendJsonMessage(
            {
                action: "get_queue",
                data: {
                    doctor_id: userData.id,
                },
            },
            false
        );
    };

    const callPatient = (patientId) => {
        sendJsonMessage(
            {
                action: "call_patient",
                data: {
                    doctor_id: userData.id,
                    medical_office_id: selectedMedicalOffice,
                    patient_id: patientId,
                },
            },
            false
        );
    };

    const skipPatient = () => {
        sendJsonMessage(
            {
                action: "mark_missed_appointment",
                data: {
                    turn_id: turnToSkip.id,
                },
            },
            false
        );
        setOpen(false);
    };

    const removeFromQueue = (turnId) => {
        setPatientQueue(patientQueue.filter((turn) => turn.id !== turnId));
    };

    const finishTurn = (turnId) => {
        sendJsonMessage(
            {
                action: "mark_attended",
                data: { turn_id: turnId },
            },
            false
        );

        setPatientQueue(
            patientQueue.map((turn) =>
                turn.id === turnId ? { ...turn, attended: true } : turn
            )
        );
    };

    useEffect(() => {
        if (!didInit) {
            didInit = true;
            getPatientQueue();
        }

        return () => {
            sendJsonMessage(
                {
                    action: "logout",
                    data: {
                        doctor_id: userData.id,
                    },
                },
                false
            );
        };
    }, []);

    useEffect(() => {
        if (!lastJsonMessage) return;

        console.log("WebSocket mensaje recibido:", lastJsonMessage);

        if (
            lastJsonMessage.type === "no_patient" &&
            lastJsonMessage.status === "pass"
        ) {
            setStep(3);
        } else if (
            lastJsonMessage.type === "connection" &&
            lastJsonMessage.status === "success" &&
            lastJsonMessage.message?.includes("Conectado a la cola")
        ) {
            // Cuando se reconecta al grupo, la cola ya fue enviada automáticamente por el backend
            console.log("Reconectado al grupo, esperando cola actualizada...");
        } else if (
            lastJsonMessage.type === "queue" &&
            lastJsonMessage.status === "success" &&
            lastJsonMessage.message === "Nuevo turno creado."
        ) {
            console.log("Nuevo turno detectado:", lastJsonMessage.data);
            // Verificar que el turno no exista ya en la cola
            const turnExists = patientQueue.some(
                (turn) => turn.id === lastJsonMessage.data.id
            );
            if (!turnExists) {
                setPatientQueue([
                    ...patientQueue,
                    { ...lastJsonMessage.data, ready: false, on_hold: false },
                ]);
            }
            // Remover de pending si el paciente llegó
            if (lastJsonMessage.data.appointment_id) {
                setPendingAppointments((prev) =>
                    prev.filter(
                        (a) => a.appointment_id !== lastJsonMessage.data.appointment_id
                    )
                );
            }
        } else if (
            lastJsonMessage.type === "queue" &&
            lastJsonMessage.status === "success" &&
            lastJsonMessage.data?.patients
        ) {
            console.log(
                "Cola sincronizada, pacientes recibidos:",
                lastJsonMessage.data.patients.length
            );
            setPatientQueue(
                lastJsonMessage.data.patients.map((patient) => {
                    return { ...patient, ready: false, on_hold: false };
                })
            );
            setPendingAppointments(
                lastJsonMessage.data.pending_appointments || []
            );
            setLastSyncTime(new Date());
            setLoading(false);
        } else if (
            lastJsonMessage.type === "appointment" &&
            lastJsonMessage.status === "success"
        ) {
            setLoading(false);
        }
    }, [lastJsonMessage]);

    const confirmation = (
        <div className="bg-white dark:bg-gray-900 text-black dark:text-white">
            <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                    <ExclamationTriangleIcon
                        className="h-6 w-6 text-yellow-800"
                        aria-hidden="true"
                    />
                </div>
                <div className="mt-3 text-center sm:mt-5">
                    <Dialog.Title
                        as="h3"
                        className="text-lg leading-6 font-medium text-black dark:text-white"
                    >
                        Confirmar
                    </Dialog.Title>
                    <div className="mt-2">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            ¿Estás seguro de que quieres marcar como ausente a
                            este paciente?
                        </p>
                    </div>
                </div>
            </div>
            <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary sm:col-start-2 sm:text-sm"
                    onClick={() => {
                        skipPatient();
                        removeFromQueue(turnToSkip.id);
                    }}
                >
                    Aceptar
                </button>
                <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:col-start-1 sm:text-sm"
                    onClick={() => {
                        setTurnToSkip(null);
                        setOpen(false);
                    }}
                >
                    Cancelar
                </button>
            </div>
        </div>
    );

    return (
        <div className="-my-2 sm:-mx-6 lg:-mx-8 w-full self-start mt-8">
            {loading ? (
                <div className="h-screen flex items-center justify-center">
                    <div className="w-10 h-10 mx-5 border-b-2 border-primary rounded-full animate-spin"></div>
                </div>
            ) : (
                <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <h1 className="text-2xl font-bold leading-9 text-gray-600 dark:text-gray-400 mb-8">
                        Gestión de turnos de consultas -{" "}
                        {`${userData.gender === 2 ? "Dra" : "Dr"}. ${
                            userData.firstName
                        } ${userData.lastName}`}
                    </h1>

                    <Modal
                        size="sm:max-w-lg w-full"
                        content={confirmation}
                        open={open}
                        setOpen={setOpen}
                    />

                    {patientQueue.length === 0 && pendingAppointments.length === 0 ? (
                        <div className="flex flex-col items-center mt-16">
                            <img
                                className="w-48 mx-auto"
                                src={logo}
                                alt="Logo"
                            />
                            <h2 className="text-center text-[30px] font-bold leading-9 text-primary mb-10">
                                Ya no quedan pacientes por atender
                            </h2>
                        </div>
                    ) : (
                        <div className="max-w-[95dvw]">
                            <CombinedTable
                                patientQueue={patientQueue}
                                pendingAppointments={pendingAppointments}
                                callPatient={callPatient}
                                setPatientQueue={setPatientQueue}
                                setTurnToSkip={setTurnToSkip}
                                finishTurn={finishTurn}
                                setOpen={setOpen}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
export default CallerList;

const CombinedTable = ({
    patientQueue,
    pendingAppointments,
    callPatient,
    setPatientQueue,
    setTurnToSkip,
    finishTurn,
    setOpen,
}) => {
    const thClass =
        "px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider";

    const formatArrivalTime = (createdAt) => {
        if (!createdAt) return "--:--";
        const [_, time] = createdAt.split(" ");
        let [hours, minutes] = time.split(":").map(Number);
        hours = (hours - 3 + 24) % 24;
        return `${hours.toString().padStart(2, "0")}:${minutes
            .toString()
            .padStart(2, "0")}`;
    };

    return (
        <div className="w-full overflow-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900 z-30 shadow-sm">
                    <tr>
                        <th scope="col" className={thClass}>Paciente</th>
                        <th scope="col" className={`${thClass} text-center`}>Hora cita</th>
                        <th scope="col" className={`${thClass} text-center`}>Hora llegada</th>
                        <th scope="col" className={`${thClass} text-center`}>Edad</th>
                        <th scope="col" className={`${thClass} text-center`}>CI</th>
                        <th scope="col" className={thClass}>Notas</th>
                        <th scope="col" className={`${thClass} text-center`}>Estado</th>
                        <th scope="col" className={thClass}></th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                    {patientQueue.map((el) => (
                        <tr
                            key={`turn-${el.id}`}
                            style={
                                el.attended
                                    ? { backgroundColor: "#9CEC5B" }
                                    : el.on_hold
                                    ? { backgroundColor: "#ffd6a8" }
                                    : {}
                            }
                        >
                            <td className="px-4 py-3 whitespace-nowrap text-left text-sm font-medium text-primary hover:text-primary">
                                <Link
                                    to={`/patients/${el.patient_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {el.patient}
                                </Link>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center text-sm text-gray-900 dark:text-white">
                                {el.scheduled_time || "--:--"}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center text-sm text-gray-900 dark:text-white">
                                {formatArrivalTime(el.created_at)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center text-sm text-gray-900 dark:text-white">
                                {el.age < 1 ? "RN" : el.age}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center text-sm text-gray-900 dark:text-white">
                                {el.gov_id}
                            </td>
                            <NotesCell notes={el.appointment_notes} />
                            <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-medium">
                                {el.attended ? (
                                    <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/10">
                                        Atendido
                                    </span>
                                ) : el.on_hold ? (
                                    <span className="inline-flex items-center rounded-md bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-600/10">
                                        En espera
                                    </span>
                                ) : el.is_urgent ? (
                                    <span className="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/10">
                                        Urgente
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/10">
                                        Presente
                                    </span>
                                )}
                            </td>
                            <td className="whitespace-nowrap px-3 py-3 text-sm">
                                {!el.attended ? (
                                    <div className="flex items-center gap-x-2 justify-end">
                                        <button
                                            onClick={() =>
                                                callPatient(el.patient_id)
                                            }
                                            className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:opacity-90 active:opacity-75 transition-opacity"
                                        >
                                            Llamar
                                        </button>
                                        <button
                                            onClick={() => finishTurn(el.id)}
                                            className="inline-flex items-center gap-1 rounded-md bg-teal-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-teal-700 active:bg-teal-800 transition-colors"
                                        >
                                            Finalizar
                                        </button>
                                        <button
                                            onClick={() =>
                                                setPatientQueue(
                                                    patientQueue.map(
                                                        (patient) => {
                                                            if (
                                                                patient.gov_id ===
                                                                el.gov_id
                                                            ) {
                                                                return {
                                                                    ...patient,
                                                                    on_hold: true,
                                                                };
                                                            }
                                                            return patient;
                                                        }
                                                    )
                                                )
                                            }
                                            className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium text-amber-600 ring-1 ring-inset ring-amber-400 hover:bg-amber-50 active:bg-amber-100 transition-colors"
                                        >
                                            En espera
                                        </button>
                                        <button
                                            onClick={() => {
                                                setTurnToSkip(el);
                                                setOpen(true);
                                            }}
                                            className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 active:bg-red-100 transition-colors"
                                        >
                                            Ausente
                                        </button>
                                    </div>
                                ) : null}
                            </td>
                        </tr>
                    ))}
                    {pendingAppointments.map((appt) => (
                        <tr
                            key={`pending-${appt.appointment_id}`}
                            className="bg-gray-50 dark:bg-gray-700/50"
                        >
                            <td className="px-4 py-3 whitespace-nowrap text-left text-sm font-medium">
                                {appt.patient_id ? (
                                    <Link
                                        to={`/patients/${appt.patient_id}`}
                                        className="text-gray-500 dark:text-gray-400 hover:text-primary"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {appt.patient}
                                    </Link>
                                ) : (
                                    <span className="text-red-800 bg-red-100 font-semibold p-1 rounded-full">
                                        Bloqueado
                                    </span>
                                )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-400">
                                {appt.scheduled_time || "--:--"}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-400">
                                --
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-400">
                                {appt.age != null ? (appt.age < 1 ? "RN" : appt.age) : "--"}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-400">
                                {appt.gov_id || "--"}
                            </td>
                            <NotesCell notes={appt.appointment_notes} />
                            <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-medium">
                                <span className="inline-flex items-center rounded-md bg-gray-100 dark:bg-gray-600 px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 ring-1 ring-inset ring-gray-500/10">
                                    No llegó
                                </span>
                            </td>
                            <td className="px-3 py-3"></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

function NotesCell({ notes }) {
    const [expanded, setExpanded] = useState(false);

    if (!notes)
        return (
            <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-200">
                --
            </td>
        );

    const cleanText = notes.replace(/<[^>]+>/g, "");
    const limit = 100;
    const shouldTruncate = cleanText.length > limit;

    return (
        <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-200 max-w-xs">
            <div>
                <div
                    className={`${
                        !expanded && "line-clamp-3 overflow-hidden"
                    } `}
                    dangerouslySetInnerHTML={{
                        __html: expanded
                            ? notes
                            : cleanText.slice(0, limit) +
                              (shouldTruncate ? "..." : ""),
                    }}
                ></div>
                {shouldTruncate && (
                    <button
                        className="text-primary hover:underline mt-1 text-xs"
                        onClick={() => setExpanded(!expanded)}
                    >
                        {expanded ? "Ver menos" : "Ver más"}
                    </button>
                )}
            </div>
        </td>
    );
}
